/**
 * Integrates the various map-related views and controls into a single interface. Provides functionality to users for viewing, creating, updating, and destroying map objects, map layers, and map services.
 *
 * ## Fields
 * Name | Description
 * --- | ---
 * workingLayer | The `WorkspaceLayer` representing the container for temporary objects.
 * propertiesWindow | A `WindowView` used to display attributes of selected objects.
 * layerWindow | A `WindowView` containing the services and layers currently available on the map.
 * legendWindow | A `WindowView` containing the services and layers currently visible on the map.
 * searchWindow | A `WindowView` containing the services and layers currently available for searching.
 * measureWindow | A `WindowView` containing measurement tools.
 * searchView | The `SearchPanelView` contained in `searchWindow`.
 * container | The `WindowView` attached to this view's DOM element.
 * mapView | The `MapView` associated with this view.
 * @module app/views/map/MapIndex
 */
import Backbone from "backbone";
import _ from "underscore";
import Keycode from "keycode";
import $ from "jquery";
import WindowView from "core/views/WindowView";
import MapView from "app/views/map/MapView";
import ButtonView from "core/views/ButtonView";
import MapLayerView from "app/views/map/MapLayerView";
import MapMeasurementView from "app/views/map/MapMeasurementView";
import MapSearchView from "app/views/map/MapSearchView";
import SearchPanelView from "app/views/map/SearchPanelView";
import SelectView from "core/views/SelectView";
import GeoUtils from "app/utils/GeoUtils";
import MapDisplayView from "app/views/map/MapDisplayView";
import ErrorUtils from "app/utils/ErrorUtils";
import $bootstrap from "bootstrap";
import ModalView from "core/views/ModalView";
import ViewHelper from "app/views/map/helpers/ViewHelper";
import HideRight from "app/views/map/helpers/HideRight.mixin";
import HideLeft from "app/views/map/helpers/HideLeft.mixin";
import velocity from "velocity-animate";
import Page from "core/Page";
import Operation from "core/models/Operation";
import session from "app/Session";
import cms from "core/context-menu-service";
import modals from "core/views/modals";
import isMobile from "core/is-mobile";
import messenger from "app/Messenger";
import BaseRouter from "core/base/Router";
import app from "app/App";
import mapContext from "app/views/map/MapContext";
import FeatureResults from "app/models/map/FeatureResults";

var clickContainerWithinMap = ".esriMapContainer";

export default Backbone.View.extend({
  className: "well panel panel-default form-group no-select map-index",

  /**
   * Initializes this view.
   * @param  {object} options The options hash.
   * @param {string} options.basemap:'gray' The basemap with which to initialize the `MapView`.
   * @param {Layout} options.layerConfiguration The layer configuration to begin with.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      basemap: "gray",
    });

    this._attachLayer = _.bind(this._attachLayer, this);
    this._attachServiceLayer = _.bind(this._attachServiceLayer, this);
    this._detachLayer = _.bind(this._detachLayer, this);
    this._detachServiceLayer = _.bind(this._detachServiceLayer, this);
    this._zoomIfOnlyService = _.bind(this._zoomIfOnlyService, this);

    this._context = mapContext;
    this._isMobile = isMobile;

    this.listenTo(this._context, "change:configuration", function () {
      this.setConfiguration(this._context.getConfiguration());
    });

    this.listenTo(this._context, "change:display", function (model) {
      if (this.mapView && this.mapView.map) {
        cms.detachContextMenuDelegate(
          this.mapView.map.root,
          clickContainerWithinMap
        );
      }

      this.setDrawContextMenu();

      if (this._isMobile) {
        if (model instanceof FeatureResults) {
          // If the user is on a mobile device and looking at a geometry.
          if (model) {
            var self = this;

            // Set new content for the modalView from the selected model.
            var modelView = ViewHelper.getViewForModel(model);
            var modelViewTitle = modelView.getTitle();

            var footerChanged = function footerChanged() {
              if (self.modalView) {
                var $footer = self.modalView.$(".modal-footer");

                $footer.html("");
                $footer.append(modelView.getFooter());

                if (!modelView.getFooter()) {
                  $footer.hide();
                }
              }
            };

            // If a model is present and no modalView, create a modal view.
            if (!this.modalView) {
              this.modalView = new ModalView({
                title: modelViewTitle,
              });
              this.modalView.$el.on("click", "button", function (evt) {
                if (self.modalView) {
                  self.modalView.hide();
                }
                self.modalView = null;
              });
            } else {
              this.modalView.$el.one("click", "button", function (evt) {
                if (self.modalView) {
                  self.modalView.hide();
                }
                self.modalView = null;
              });
            }
            this.listenTo(modelView, "footer-changed", footerChanged);
            this.modalView.render();
            this.modalView.setContent(modelView.$el);
            footerChanged();
            this.modalView.show();
          } else {
            // If a modalView is present without a model, remove the modalView.
            if (this.modalView) {
              this.modalView.hide();
              this.modalView = null;
            }
          }
        } else {
          // if the user is on a mobile device and wants to display a layer or service, zoom to it
          this._context.getOperation("zoom-to-item").action(model);
        }
      } else {
        if (this.propertiesWindow && model) {
          this.propertiesWindow.show();
        } else {
          this.propertiesWindow.hide();
        }
      }
    });

    this.listenTo(this._context.operations, "clear-selection", function () {
      this.setDrawContextMenu();
    });

    // evidently, this is the only way to listen to events triggered by an operation in one of our operations files
    this.listenTo(
      this._context.getOperation("export-geometry-table").unbind(),
      "results-window-maximized results-window-restored",
      function (windowView) {
        this._checkIntercomPosition(windowView.isMaximized());
      }
    );

    this.setConfiguration(this._context.getConfiguration()); //must be down here so that the attach/detach functions have the right scope
    this.listenToOnce(BaseRouter.current, "leave", function () {
      this.setConfiguration(null);
      this.stopListening();
    });
  },

  _checkIntercomPosition: function (resultsWindowIsMaximized) {
    var windowView;

    if (this.propertiesWindow.isVisible()) {
      windowView = this.propertiesWindow;
    }

    if (this.searchWindow.isVisible()) {
      windowView = this.searchWindow;
    }

    if (windowView) {
      if (resultsWindowIsMaximized) {
        this._setIntercomPosition(false, null);
      } else {
        this._setIntercomPosition(true, windowView);
      }
    }
  },

  /**
   * Sets the configuration for this view.
   * @param {Layout} configuration The layer configuration.
   */
  setConfiguration: function setConfiguration(configuration) {
    if (this._config) {
      this._detachLayout(this._config);
    }

    this._config = configuration;

    if (this._config) {
      this._attachLayout(this._config);
    }

    if (this.mapView) {
      this.mapView.setConfiguration(configuration);
    }

    if (this.searchView) {
      this.searchView.setConfiguration(configuration);
    }

    if (this._layerView) {
      this._layerView.setConfiguration(configuration);
    }
  },

  /**
   * Gets the configuration for this view.
   * @return {Layout} The layer configuration.
   */
  getConfiguration: function () {
    return this._context.getConfiguration();
  },

  /**
   * Gets the currently selected service for this view.
   * @return {MapServiceBase} The selected service, or null if none is selected.
   */
  getSelectedService: function () {
    return this.getConfiguration().getActiveService();
  },

  /**
   * Gets the currently selected layer for this view.
   * @return {MapLayerBase} The selected layer, or null if none is selected.
   */
  getSelectedLayer: function () {
    return this.getConfiguration().getActiveLayer();
  },

  /**
   * Gets the active drawing layer.
   * @return {MapLayerBase} The active drawing layer.
   */
  getActiveLayer: function () {
    return this.getConfiguration().getDrawingLayer();
  },

  /**
   * Creates a detail window and displays it in the UI.
   * @param  {MapDetailView} contentView The view to be placed in the window.
   * @param  {WindowView} display     An alternative window to use for properties. By default, this is `propertiesWindow`.
   * @param {Boolean} hideInitially True to hide the window initially, otherwise false.
   * @return {WindowView}             The detail window. The window should *not* be removed from the DOM. If not desired, it should be hidden.
   */
  createDetailWindow: function (contentView, display, hideInitially) {
    var dw = display || this.propertiesWindow;
    var self = this;

    function clearToolbar() {
      dw.clearControls();
    }

    function refreshWindow() {
      dw.setContent(dw.contentView.$el);
      dw.header.setTitle(dw.contentView.getTitle());
      dw.setFooter(dw.contentView.getFooter());

      _.each(dw.contentView.getControls(), function (control) {
        dw.header.getRightControlBar().prepend(control);
      });

      if (!hideInitially) {
        dw.show();
      }
      dw.refresh();

      self.stopListening(
        contentView,
        "title-changed footer-changed controls-changed",
        refreshWindow
      );
      self.listenTo(
        contentView,
        "title-changed footer-changed controls-changed",
        refreshWindow
      );

      self.stopListening(contentView, "controls-cleared", clearToolbar);
      self.listenTo(contentView, "controls-cleared", clearToolbar);
    }

    dw.contentView = contentView;
    clearToolbar();
    refreshWindow();

    return dw;
  },

  /**
   * Refreshes the displayed detail window.
   * @param  {WindowView} display An alternative window to refresh. By default, this is `propertiesWindow`.
   * @return {WindowView}         The detail window.
   */
  refreshDetailWindow: function (display) {
    var dw = display || this.propertiesWindow;

    dw.setContent(dw.contentView.$el);
    dw.header.setTitle(dw.contentView.getTitle());
    dw.setFooter(dw.contentView.getFooter());
    dw.contentView.refreshControls();

    dw.refresh();
  },

  _showModel: function (model, layer) {
    var context = this._context;

    model.originalLayer = layer;
    context.setSelection([model]);
    context
      .getOperation("highlight-geometry")
      .action()
      .then(function () {
        return context.getOperation("show-geometry-details").action();
      });
  },

  _createView: function () {
    return WindowView.register(
      new WindowView({
        maximized: true,
        allowMaximize: false,
        allowClose: false,
        allowResize: false,
        allowMove: false,
        title: "",
        "z-index": 1,
        el: this.$el,
      })
    );
  },

  _createMapView: function () {
    var geometryEngine = window.require("esri/geometry/geometryEngine");
    var mapView = new MapView({
      geocoder: true,
      edit: true,
      draw: true,
      basemap: this.options.basemap,
      scalebar: {
        attachTo: "bottom-center",
      },
    });

    this.listenTo(mapView, "models-selected", function (
      evt,
      graphic,
      point,
      models
    ) {
      this._modelsSelected(evt, graphic, point, models);
    });

    this.listenTo(mapView, "activate", function () {
      this.setDrawContextMenu();
    });

    this.listenTo(mapView, "deactivate", function () {
      this.setDrawContextMenu();
    });

    return mapView;
  },

  _createPropertiesViewToggle: function (view) {
    if (this.propertiesWindow) {
      this.propertiesWindow.remove();
      this.propertiesWindow = null;
    }

    var self = this;
    var opts = {
      allowMaximize: false,
      hideOnClose: true,
      anchor: {
        top: true,
        bottom: true,
        right: true,
        container: view.$el,
      },
      footer: true,
      title: "Properties",
    };

    this.propertiesWindow = new MapDisplayView(opts);

    this.propertiesWindow.render();

    $("body").append(this.propertiesWindow.$el);

    WindowView.register(this.propertiesWindow);

    this.propertiesWindow.$el.addClass("properties-window");

    _.extend(this.propertiesWindow, HideRight);

    var showPropertiesViewButton = new ButtonView({
      text: "Properties",
      className: "properties-button",
      click: _.bind(function () {
        this.propertiesWindow.toggle();
      }, this),
    });

    this.listenTo(this.propertiesWindow, "hiding", function () {
      if (self.propertiesWindow.isVisible()) {
        showPropertiesViewButton.deactivate();
        self._setIntercomPosition(false, self.propertiesWindow);
        self._moveZoomTowardsRight();
      }
    });

    this.listenTo(this.propertiesWindow, "showing", function () {
      if (!self.propertiesWindow.isVisible()) {
        self.searchWindow.hide();
        self._moveZoomTowardsCenter();
        self._setIntercomPosition(true, self.propertiesWindow);
      }
      showPropertiesViewButton.activate();
    });

    this.propertiesWindow.$el.on("resize", function () {
      self._setIntercomPosition(true, self.propertiesWindow);
    });

    return showPropertiesViewButton;
  },

  /**
   * Moves the esri simple slider, which zooms in/out of the map, towards the center of the map.
   * Called when search or properties panel are in use, so zoom is no longer hidden
   * @return {void};
   */
  _moveZoomTowardsCenter: function () {
    var $zoomSlider = this.$(".esriSimpleSlider");
    var $zoomToUserButton = this.$(".zoom-button");
    var els = [];

    $zoomToUserButton.each(function () {
      els.push(this);
    });

    if ($zoomSlider.length) {
      els.push($zoomSlider[0]);
    }

    velocity(
      els,
      {
        left: Page.getWidth() - 270 - 34,
      },
      {
        duration: 200,
        queue: false,
      }
    );
  },

  /**
   * Moves the esri simple slider, which zooms in/out of the map, towards the right of the map.
   * Called when properties or search pane are collapsed.
   * @return {void};
   */
  _moveZoomTowardsRight: function () {
    var $zoomSlider = this.$(".esriSimpleSlider");
    var $zoomToUserButton = this.$(".zoom-button");
    var els = [];

    $zoomToUserButton.each(function () {
      els.push(this);
    });

    if ($zoomSlider.length) {
      els.push($zoomSlider[0]);
    }

    velocity(
      els,
      {
        left: Page.getWidth() - 20 - 34,
      },
      {
        duration: 200,
        queue: false,
      }
    );
  },

  _createLayerViewToggle: function (view) {
    if (this.layerWindow) {
      this._layerView.off();
      this._layerView.stopListening();
      this.layerWindow.remove();
      this.layerWindow = null;
    }

    var layerView = (this._layerView = new MapLayerView());

    layerView.setTitle("Layers");

    this.listenTo(this._context.operations, "show-geometry-details", function (
      models
    ) {
      messenger.info("Search found " + models.length + " results.");
    });

    this.layerWindow = WindowView.create("Layers", {
      allowMaximize: false,
      hideOnClose: true,
      allowClose: !isMobile,
      anchor: {
        top: true,
        bottom: true,
        left: true,
        container: view.$el,
      },
      footer: true,
    });
    this.layerWindow.$el.addClass("layer-window");

    _.extend(this.layerWindow, HideLeft);

    this.createDetailWindow(layerView, this.layerWindow, true).hide();

    var refreshOnce = _.once(
      _.bind(this.layerWindow.refresh, this.layerWindow)
    );

    var showLayerViewButton = new ButtonView({
      text: "Layers",
      className: "layers-button",
      click: _.bind(function () {
        this.layerWindow.toggle();

        refreshOnce();
      }, this),
    });

    this.listenTo(this.layerWindow, "hidden", function () {
      showLayerViewButton.deactivate();
    });

    this.listenTo(this.layerWindow, "shown", function () {
      showLayerViewButton.activate();
    });

    return showLayerViewButton;
  },

  /**
   * A function which adjusts the intercom position so it won't interfere with what is displayed in panels
   * @param windowShown {boolean}if the window is being shown or hidden
   * @param windowView {WindowView}the window for which the intercom position should be adjusted for
   * @private
   */
  _setIntercomPosition: function (windowShown, windowView) {
    var bufferPixels = 10; //a buffer of pixels for aesthetics
    var $intercomControls = $(".intercom-app iframe");

    if (windowShown) {
      $intercomControls.css({
        right: windowView.$el.width() + bufferPixels,
        left: "",
      });
    } else {
      //reset to the original location
      $intercomControls.css({
        right: "",
        left: "auto",
      });
    }
  },

  _createZoomButton: function (view, mapView) {
    return new ButtonView({
      text: "",
      className: "zoom-button",
      icon: "screenshot",
      click: _.bind(function () {
        this._zoomAndTrack(mapView);
      }, this),
    });
  },

  _createSearchViewToggle: function (view) {
    if (this.searchWindow) {
      this.searchWindow.remove();
      this.searchWindow = null;
    }

    var self = this;

    this.searchWindow = WindowView.create("Search", {
      allowMaximize: false,
      allowClose: !isMobile,
      hideOnClose: true,
      anchor: {
        top: true,
        bottom: true,
        right: true,
        container: view.$el,
      },
    });

    var searchViewButton = new ButtonView({
      text: "Search",
      className: "search-button",
      click: _.bind(function () {
        this.searchWindow.toggle();
      }, this),
    });

    this.listenTo(this.searchWindow, "hiding", function () {
      if (self.searchWindow.isVisible()) {
        self._moveZoomTowardsRight();
        searchViewButton.deactivate();
        self._setIntercomPosition(false, self.searchWindow);
      }
    });

    this.listenToOnce(this.searchWindow, "showing", function () {
      self.searchWindow.$el.css(
        "top",
        self.searchWindow._containerDimensions().top
      ); //we're cheating here
    });

    this.listenTo(this.searchWindow, "showing", function () {
      if (!self.searchWindow.isVisible()) {
        self.propertiesWindow.hide();
        self._moveZoomTowardsCenter();
        self._setIntercomPosition(true, self.searchWindow);
      }
      searchViewButton.activate();
    });

    this.searchWindow.$el.on("resize", function () {
      self._setIntercomPosition(true, self.searchWindow);
    });

    this.searchWindow.$el.addClass("search-window");

    _.extend(this.searchWindow, HideRight);

    return searchViewButton;
  },

  _createMeasureViewToggle: function (view, mapView) {
    if (this.measureWindow) {
      this._measureView.off();
      this._measureView.stopListening();
      this.measureWindow.remove();
      this.measureWindow = null;
    }

    this.measureWindow = WindowView.create("Measure Tool", {
      allowMaximize: false,
      hideOnClose: true,
      anchor: {
        container: mapView.$el,
      },
      shadow: true,
    });
    this.measureWindow.size(null, 350);

    var measureView = (this._measureView = new MapMeasurementView({
      mapView: mapView,
    }));
    var self = this;

    measureView.render();
    this.measureWindow.setContent(measureView.$el);

    var showMeasureView = (this._showMeasureView = new SelectView({
      items: [
        {
          text: "Measure",
          value: "",
        },
        {
          text: "Location",
          value: "location",
        },
        {
          text: "Distance",
          value: "distance",
        },
        {
          text: "Area",
          value: "area",
        },
      ],
      class: "measurementSelector",
    }));

    this.listenTo(showMeasureView, "change", function (value) {
      var configure = true;

      switch (value) {
        case "location":
          self.measureWindow.setHeader(
            "Measure - Location",
            true,
            true,
            false,
            true
          );
          break;

        case "distance":
          self.measureWindow.setHeader(
            "Measure - Distance",
            true,
            true,
            false,
            true
          );
          break;

        case "area":
          self.measureWindow.setHeader(
            "Measure - Area",
            true,
            true,
            false,
            true
          );
          break;
        default:
          configure = false;
          break;
      }

      if (configure) {
        self._activateMeasure(value);
        measureView.configureMeasurement(value, self);
      }

      showMeasureView.value = "";
    });

    this.listenTo(
      this.measureWindow,
      "close",
      function () {
        self._deactivateMeasure();
      },
      this
    );

    this.measureWindow.$el.addClass("measurement-window");

    return showMeasureView;
  },

  _deactivateMeasure: function () {
    this._measureView.resetMeasurement();

    this._drawView.enable(); //allow drawing again
    this._showMeasureView.enable(); //allow measuring again
    this.mapView.isMeasuring(false); //clear the isMeasuring function
    this.measureWindow.hide();
  },

  _activateMeasure: function (type) {
    if (this.isDrawingActive()) {
      this.deactivateDraw();
    }

    this._measureView.configureMeasurement(type);

    this._drawView.disable();
    this._showMeasureView.disable();
    this.mapView.isMeasuring(true);
    this.measureWindow.show();
    this.measureWindow.bringToFront(); //bring the measure window to the front

    var centerOnce =
      this.measureWindow._center ||
      _.once(
        _.bind(function () {
          this.measureWindow.center();
        }, this)
      );

    this.measureWindow._center = centerOnce;

    centerOnce();
  },

  _createDrawView: function (view, mapView) {
    var DrawToolbar = window.require("esri/toolbars/draw");
    var options = {};
    var self = this;

    options[DrawToolbar.POINT] = "Point";
    options[DrawToolbar.MULTI_POINT] = "Point List";
    options[DrawToolbar.POLYGON] = "Polygon";
    options[DrawToolbar.POLYLINE] = "Line";
    options[DrawToolbar.CIRCLE] = "Circle";

    var drawOptions = [
      {
        text: "Draw",
        value: "",
      },
    ];

    _.each(options, function (name, key) {
      drawOptions.push({
        text: name,
        value: key,
      });
    });

    var drawView = new SelectView({
      items: drawOptions,
      class: "draw-selection",
    });

    this.listenTo(drawView, "change", function (value) {
      mapView.activateDraw(value, self.getConfiguration().getDrawingLayer());
      drawView.value = ""; //reset selection
    });

    return drawView;
  },

  _createBasemapSelectorView: function _createBasemapSelectorView(
    view,
    mapView
  ) {
    var selectView = new SelectView({
      value: this.options.basemap,
      items: [
        {
          value: "streets",
          text: "Streets",
        },
        {
          value: "satellite",
          text: "Satellite",
        },
        {
          value: "hybrid",
          text: "Hybrid",
        },
        {
          value: "topo",
          text: "Topography",
        },
        {
          value: "gray",
          text: "Gray",
        },
        {
          value: "osm",
          text: "Open Street Map",
        },
      ],
    });

    this.listenTo(selectView, "change", function (value) {
      mapView.setBaseMap(value);
    });

    return selectView;
  },

  _createSearchView: function () {
    if (this.searchView) {
      this.searchView.off();
      this.searchView.stopListening();
    }

    var searchView = (this.searchView = new SearchPanelView({
      mapContainer: this,
      allowSearch: false,
      allowKeySearch: true,
    }));
    var self = this;

    searchView.render();
    self.searchWindow.setContent(searchView.$el);

    return searchView;
  },

  setDrawContextMenu: function () {
    var DrawToolbar = window.require("esri/toolbars/draw");
    var self = this;

    var drawTypes = {
      "Draw Point": DrawToolbar.POINT,
      "Draw Line": DrawToolbar.POLYLINE,
      "Draw Polygon": DrawToolbar.POLYGON,
      "Draw Circle": DrawToolbar.CIRCLE,
    };

    var contextMenu = [self._context.getOperation("paste"), true];

    if (self.mapView.isDrawingActive()) {
      contextMenu = contextMenu.concat([
        {
          text: "Complete Drawing",
          action: function () {
            self._context.getOperation("commit-drawing").action();
          },
        },
        {
          text: "Cancel Drawing",
          action: function () {
            self._context.getOperation("cancel-drawing").action();
          },
        },
      ]);
    } else {
      contextMenu = contextMenu.concat(
        _.map(drawTypes, function (type, key) {
          return {
            text: key,
            action: function () {
              self.mapView.activateDraw(
                type,
                self.getConfiguration().getDrawingLayer()
              );
            },
          };
        })
      );

      contextMenu = contextMenu.concat([
        true,
        {
          text: "Enable Snapping",
          checkable: true,
          checked: !!self.mapView.map.snappingManager,
          action: function () {
            if (self.mapView.map.snappingManager) {
              self.mapView.map.disableSnapping();
            } else {
              self.mapView.map.enableSnapping({
                alwaysSnap: true,
              });
            }
          },
        },
      ]);
    }

    cms.detachContextMenuDelegate(
      self.mapView.map.root,
      clickContainerWithinMap
    );
    cms.attachContextMenu(
      clickContainerWithinMap,
      contextMenu,
      self.mapView.map.root
    );
  },

  render: function () {
    app.disableScroll(); //trigger the disable scroll event
    if (this.mapView) {
      this.mapView.stopListening();
      this.mapView.remove();
      this.stopListening(this.mapView);
    }
    if (this.container) {
      this.container.stopListening();
    }

    var view = (this.container = this._createView());
    var mapView = (this.mapView = this._createMapView());

    if (!mapView.$el.parent().length) {
      $('<div class="hide"></div>').appendTo("body").append(mapView.$el); //map needs an element in the DOM to work right
    }

    view.render();
    view.$(".panel-body").css("padding", 0);
    mapView.render();
    mapContext.setMap(mapView.map);
    view.setContent(mapView.$el);
    this.listenTo(view, "resize", function (width, height) {
      mapView.resetHeight(height - view.header.$el.outerHeight(true));
    });
    this.listenTo(Page, "update", function () {
      self._moveZoomTowardsRight();
    });
    var self = this;

    var oldBasemap;
    var skipBasemapChange = false;
    var basemapChanging = false;

    function verifyBasemap() {
      var result = true;
      var maxScales = 0;

      _.each(
        _.map(mapView.map.basemapLayerIds, function (layerId) {
          return mapView.map.getLayer(layerId);
        }),
        function (baselayer) {
          if (maxScales < baselayer.scales.length) {
            maxScales = baselayer.scales.length;
          }
        }
      );

      if (maxScales <= mapView.map.getZoom()) {
        result = false;
      }

      return result;
    }

    function issueBasemapWarning() {
      var oldDisplay = self._basemapWarningView.$el.css("display");

      self._basemapWarningView.$el.show();

      if (oldDisplay == "none") {
        messenger.warning(
          "You have zoomed beyond the limits of available imagery."
        );
      }
    }

    mapView.map.on("zoom-end", function (evt) {
      var level = evt.level;

      if (level > 16 && mapView.getBaseMap() == "gray") {
        oldBasemap = "gray";
        skipBasemapChange = true;
        basemapChanging = true;
        mapView.setBaseMap("topo");
        self._basemapSelectorView.value = "topo";
      } else {
        if (oldBasemap && level <= 16) {
          skipBasemapChange = false;
          basemapChanging = true;
          mapView.setBaseMap(oldBasemap);
          self._basemapSelectorView.value = oldBasemap;
        }
      }

      if (!basemapChanging && !verifyBasemap()) {
        issueBasemapWarning();
      } else {
        self._basemapWarningView.$el.hide();
      }
    });

    mapView.map.on("basemap-change", function () {
      if (!skipBasemapChange) {
        oldBasemap = undefined;
        skipBasemapChange = false;
      }

      basemapChanging = false;

      if (!verifyBasemap()) {
        issueBasemapWarning();
      } else {
        self._basemapWarningView.$el.hide();
      }
    });

    mapView.map.on("click", function (evt) {
      if (!evt.graphic) {
        self._clearSelection();
        self._context.clearSelection();
        self._context.clearSearchRegion();
        self.propertiesWindow.hide();

        if (isMobile) {
          self.layerWindow.hide();
          self.searchWindow.hide();
        }
      }

      if (!self._shiftKeyDown && !self._isSpatial(evt)) {
        self._context.clearSelection();
      }
    });

    // @todo refactor these each into their own handler (ie: on.('delete', doStuff))
    var onKeyDown = function (evt) {
      var el = document.activeElement;

      //do not process key commands if in a field
      if (
        !(
          el &&
          ((el.tagName.toLowerCase() == "input" && el.type == "text") ||
            el.tagName.toLowerCase() == "textarea")
        )
      ) {
        if (evt.keyCode == "8" || evt.keyCode == Keycode.backspace) {
          self._context.getOperation("remove-geometry").action();
        }

        if (evt.ctrlKey || self._ctrlKeyDown) {
          //67 is "C"
          //86 is "V"
          if (evt.keyCode == 67) {
            self._context.getOperation("copy").action();
          } else if (evt.keyCode == 86) {
            self._context.getOperation("paste").action();
          }
        }

        if (self.isDrawingActive()) {
          if (evt.keyCode == 27) {
            //escape
            self._context.getOperation("cancel-drawing").action();
          } else if (evt.keyCode == 13) {
            //enter
            self._context.getOperation("commit-drawing").action();
          }
        }
      }
    };

    mapView.map.on("key-down", onKeyDown);

    $(document).on("keydown", function (evt) {
      if (evt.keyCode == 17) {
        //control key
        self._ctrlKeyDown = true;
      }

      if (evt.keyCode == 16) {
        //shift key
        self._shiftKeyDown = true;
      }
    });

    $(document).on("keyup", function (evt) {
      if (evt.keyCode == 17) {
        //control key
        self._ctrlKeyDown = false;
      }

      if (evt.keyCode == 16) {
        //shift key
        self._shiftKeyDown = false;
      }
    });

    var WarningView = Backbone.View.extend({
      tagName: "div",
      template: _.template(
        '<span class="glyphicon glyphicon-alert" data-toggle="tooltip" data-placement="bottom" title="The map is zoomed beyond available imagery.">&nbsp;</span>'
      ),
      events: {
        click: function () {
          modals.alert(
            "Zoom Warning",
            "The map is zoomed beyond the limits of available imagery. Geometries may still be visible, but there is no base imagery for geographical reference. Zoom out to repopulate imagery.",
            "OK"
          );
        },
      },
      render: function () {
        this.$el.html(this.template());
        this.$el.css({
          display: "inline",
        });
        $bootstrap(this.$("span")).tooltip();
        this.$el.hide();
      },
    });

    var layerViewToggle = this._createLayerViewToggle(view, mapView);
    var searchViewToggle = this._createSearchViewToggle(view, mapView);
    var measureViewToggle = this._createMeasureViewToggle(view, mapView);
    var drawView = (this._drawView = this._createDrawView(view, mapView));
    var zoomButtonView = this._createZoomButton(view, mapView);
    var basemapSelectorView = (this._basemapSelectorView = this._createBasemapSelectorView(
      view,
      mapView
    ));
    var propertiesViewToggle = this._createPropertiesViewToggle(view, mapView);
    var basemapWarningView = (this._basemapWarningView = new WarningView());
    var mapSearchView = (this._searchView = new MapSearchView({
      map: mapView.map,
    }));

    this._createSearchView(view, mapView);

    this._layerView.render(); //created in _createLayerViewToggle

    _.each(
      [
        layerViewToggle,
        searchViewToggle,
        measureViewToggle,
        drawView,
        propertiesViewToggle,
        zoomButtonView,
        basemapSelectorView,
        basemapWarningView,
      ],
      function (view) {
        view.render();
      }
    );

    var left = view.header.getLeftControlBar();
    var right = view.header.getRightControlBar();

    left.append(basemapWarningView.$el);
    left.append(layerViewToggle.$el);
    left.append(basemapSelectorView.$el);
    left.append(mapSearchView.$el);

    right.append(drawView.$el);
    right.append(measureViewToggle.$el);
    if (!isMobile) {
      right.append(propertiesViewToggle.$el);
    }
    right.append(searchViewToggle.$el);

    mapView.$(clickContainerWithinMap).append(zoomButtonView.$el);

    BaseRouter.current.once("leave", function () {
      self.layerWindow.remove();
      self.measureWindow.remove();
      self.searchWindow.remove();
      self.propertiesWindow.remove();
      $("body").off("keydown", onKeyDown);
      //$('body').off('keyup', onKeyUp);
      self.stopListening();
    });

    //var modal = App.modal('Loading Services', 'Please be patient while services load.');
    modals.loading.show("Loading Services");

    var waitPrompt;

    if (!!this._loadingTimeout) {
      clearTimeout(this._loadingTimeout);
    }

    this._loadingTimeout = setTimeout(function () {
      modals.loading.hide();

      waitPrompt = modals.confirm(
        "Loading Timeout",
        'The services are taking a long time to load. Generally, this is because a large amount of data is being loaded, but it <i>may</i> indicate an error. Select "Wait" to continue waiting for things to load, or "Cancel" to return to the main page.',
        {
          type: "primary",
          text: "Wait",
        },
        function () {
          modals.loading.show("Loading Services");
        },
        "Cancel",
        function () {
          BaseRouter.current.back();
        }
      );
    }, 60000); //1 minute

    this.listenToOnce(BaseRouter.current, "leave", function () {
      clearTimeout(this._loadingTimeout);
    });

    if (mapView.map.loaded) {
      onMapLoad.call(this, mapView);
    } else {
      this.listenToOnce(mapView, "mapload", function () {
        onMapLoad.call(this, mapView);
      });
    }

    function onMapLoad(mapView) {
      var self = this;
      mapView.resetHeight(view.$el.height() - view.header.$el.outerHeight());
      mapSearchView.render();
      mapSearchView.geocoder.on("focus", function () {
        self.layerWindow.hide();
      });

      _.defer(function () {
        //self._attachLayout(self.getConfiguration());
        self
          .getConfiguration()
          .loadConfiguration()
          .done(function () {
            //self.setConfiguration(self.getConfiguration());
            modals.loading.hide();

            if (waitPrompt) {
              waitPrompt.hide();
            }

            clearTimeout(self._loadingTimeout);
            self._updateExtent(
              GeoUtils.expandExtentForView(
                GeoUtils.unionExtents(self.getConfiguration().serviceLayers)
              )
            );
            self._attachLayerView(self._layerView, mapView);
            self.setDrawContextMenu();
            self._context.display(false);
            self._moveZoomTowardsRight();
          })
          .fail(
            ErrorUtils.errorHandler({
              activity: "loading the map",
              cleanUp: function () {
                modals.loading.hide();
                clearTimeout(self._loadingTimeout);
              },
            })
          );
      });
    }

    view.show();
    view.maximize();
    window.mapContainer = this;
  },

  _zoomAndTrack: function (mapView) {
    var self = this;

    if (window && window.navigator) {
      var MapPoint = window.require("esri/geometry/Point");

      var locationError = function locationError(error) {
        var warningMessage;
        //error occurred so stop watchPosition
        if (navigator.geolocation) {
          navigator.geolocation.clearWatch(self.watchId);
          self.watchId = null;
        }
        switch (error.code) {
          case error.PERMISSION_DENIED:
            warningMessage = "Location not provided";
            break;

          case error.POSITION_UNAVAILABLE:
            warningMessage = "Current location not available";
            break;

          case error.TIMEOUT:
            warningMessage = "Timeout While Searching for Location";
            break;

          default:
            warningMessage = "Retrieving Your Location";
            break;
        }

        messenger.warning(warningMessage);
      };

      var centerAndZoom = function centerAndZoom(long, lat) {
        var desiredBasemap = "satellite";

        mapView.map.centerAndZoom(new MapPoint(long, lat), 20);
        mapView.setBaseMap(desiredBasemap);
        self._basemapSelectorView.value = desiredBasemap;
      };

      if (self.userLocation && self.userLocation.coords) {
        centerAndZoom(
          self.userLocation.coords.longitude,
          self.userLocation.coords.latitude
        );
      } else {
        window.navigator.geolocation.getCurrentPosition(
          function (loc) {
            self.userLocation = loc;
            var coordinates = self.userLocation.coords;

            if (coordinates) {
              centerAndZoom(coordinates.longitude, coordinates.latitude);
            }
          },
          locationError,
          { timeout: 10000 }
        );
      }
      if (!self.watchId) {
        var Graphic = window.require("esri/graphic");
        var SimpleMarkerSymbol = window.require(
          "esri/symbols/SimpleMarkerSymbol"
        );
        var SimpleLineSymbol = window.require("esri/symbols/SimpleLineSymbol");
        var Color = window.require("esri/Color");

        self.watchId = window.navigator.geolocation.watchPosition(function (
          loc
        ) {
          self.userLocation = loc;
          var coordinates = self.userLocation.coords;

          if (coordinates) {
            var mapPoint = new MapPoint(
              coordinates.longitude,
              coordinates.latitude
            );

            if (self.locationGraphic) {
              self.locationGraphic.setGeometry(mapPoint);
            } else {
              var symbol = new SimpleMarkerSymbol(
                SimpleMarkerSymbol.STYLE_CIRCLE,
                12,
                new SimpleLineSymbol(
                  SimpleLineSymbol.STYLE_SOLID,
                  new Color([210, 105, 30, 0.5]),
                  8
                ),
                new Color([210, 105, 30, 0.9])
              );
              self.locationGraphic = new Graphic(mapPoint, symbol);

              mapView.map.graphics.add(self.locationGraphic);
            }
          } else {
            locationError({});
          }
        },
        locationError);
      }
    }
  },

  _updateExtent: function (ext) {
    var Extent = window.require("esri/geometry/Extent");
    var self = this;
    var fit = true;

    if (
      !ext ||
      _.isNaN(ext.xmin) ||
      _.isNaN(ext.xmax) ||
      _.isNaN(ext.ymin) ||
      _.isNaN(ext.ymax) ||
      ext.spatialReference.wkid != self.mapView.map.spatialReference.wkid
    ) {
      ext = new Extent(
        -16251105.64911332, //xmin
        892867.8720825952, //ymin
        -4960439.327056367, //xmax
        8798291.085446563, //ymax
        self.mapView.map.spatialReference //spatial reference
      );
      fit = false;
    }

    self.mapView.resetHeight(
      self.container.$el.height() - self.container.header.$el.outerHeight()
    );
    self.mapView.map.setExtent(ext, fit);
    self.mapView.map.centerAt(ext.getCenter());

    _.defer(function () {
      self.mapView.map.setExtent(ext, fit);
      self.mapView.map.centerAt(ext.getCenter());
      self.mapView.refresh();
      self.$el.addClass("map-loaded");
    });
  },

  _isSpatial: function (evt) {
    return (
      (evt.ctrlKey || evt.metaKey) &&
      this.getConfiguration().getSelectedLayers().length
    );
  },

  _createVertexRemoveOperation: function (graphic, point, model) {
    var context = this._context;
    var DISTANCE_MULTIPLIER = 0.0045; //experimentally-obtained constant - it "felt right"
    var geometryEngine = window.require("esri/geometry/geometryEngine");
    var maxDistance = DISTANCE_MULTIPLIER * context.getMap().getScale(); // I have no idea what the units are supposed to be for this
    var vertexInfo;
    var self = this;
    var nearestVertices = [];
    var geometry = graphic.geometry;
    var paths = [];
    var minPoints = 0;
    var structureIndex = -1;
    var validVertex = false;

    if (geometry) {
      if (geometry.type == "polyline") {
        paths = geometry.paths;
        minPoints = 2;
      } else if (geometry.type == "polygon") {
        paths = geometry.rings;
        minPoints = 3;
      }
    }

    var available = function () {
      return validVertex;
    };

    var action = function (model) {
      var deferred = new $.Deferred();

      //find the vertex in the geometry provided, remove it, and update the model. also figure out what we need to do to refresh the editing controls

      geometry.removePoint(structureIndex, vertexInfo.vertexIndex);

      model.getFeature().getGeometry().parseEsriGeometry(geometry);

      self.mapView.deactivateEdit(true);

      if (model.getFeature().isNew()) {
        deferred.resolve(model, "Vertex successfully removed");
      } else {
        context
          .getOperation("commit-geometry")
          .action([model])
          .fail(function (err) {
            deferred.reject(err);
          })
          .done(function () {
            deferred.resolve(model, "Vertex successfully removed");
          });
      }

      return deferred.promise();
    };
    var modelGetter = function () {
      return model;
    };

    try {
      nearestVertices = geometryEngine.nearestVertices(
        graphic.geometry,
        point,
        maxDistance,
        1
      ); //only want max of 1 vertex
    } catch (err) {
      console.error(err);
      nearestVertices = [];
    }

    if (nearestVertices && nearestVertices.length) {
      vertexInfo = nearestVertices[0];

      if (vertexInfo && !vertexInfo.isEmpty) {
        var vertexFound = false;

        for (var i = 0; i < paths.length; i++) {
          var j = vertexInfo.vertexIndex;

          if (j < paths[i].length) {
            var pt = geometry.getPoint(i, j);

            if (
              pt.x == vertexInfo.coordinate.x &&
              pt.y == vertexInfo.coordinate.y
            ) {
              vertexFound = true;
              structureIndex = i;
            }
          }

          if (vertexFound) {
            if (paths[structureIndex].length > minPoints) {
              validVertex = true;
            }

            break;
          }
        }
      }
    }

    var op = new Operation({
      id: "remove-geometry-vertex",
      text: "Remove Vertex",
      action: action,
      available: available,
    });

    return op.bind(
      modelGetter,
      function (model, message) {
        if (message) {
          messenger.success(message);
        }
      },
      ErrorUtils.errorHandler()
    );
  },

  _createCloseMenuOperation: function () {
    var op = new Operation({
      id: "close-menu",
      text: "Close Menu",
      icon: "remove-sign",
      action: function () {
        return Operation.resolve();
      },
      available: function () {
        return true;
      },
    });

    return op.bind(
      function () {},
      function (model, message) {
        if (message) {
          messenger.success(message);
        }
      },
      ErrorUtils.errorHandler()
    );
  },

  _modelsSelected: function (evt, graphic, point, models) {
    //if multiple models are selected, just display them
    var self = this;

    if (self._shiftKeyDown) {
      models = models.concat(self._context.getSelection().models);
    }

    models = _.unique(models, function (model) {
      return model.id || model.cid || model;
    });

    self._context.setSelection(models);

    if (models.length > 1) {
      if (self._shiftKeyDown) {
        self._context.display(self._context.getSelection());
        self._context.getOperation("highlight-geometry").action();
      } else {
        self._context.getOperation("show-geometry-details").action();
        self._context.getOperation("highlight-geometry").action();
      }
    } else {
      //let's just assume we have exactly one model here.
      if (this._isSpatial(evt)) {
        self._context.getOperation("run-query").action();
      } else {
        if (!isMobile) {
          self._context.getOperation("show-geometry-details").action();
        }

        self._context.getOperation("highlight-geometry").action();
      }
    }

    if (models.length) {
      cms.detachContextMenuDelegate(
        self.mapView.map.root,
        clickContainerWithinMap
      );

      var mobileContext = [];

      if (isMobile) {
        var firstOperation = self._context
          .getOperation("show-geometry-details")
          .withModel(models);
        var firstModel = models[0];

        var isExternal =
          firstModel.originalLayer &&
          firstModel.originalLayer.serviceLayer &&
          firstModel.originalLayer.serviceLayer.service.get("external");

        if (!isExternal && firstModel.isNew()) {
          firstOperation = self._context
            .getOperation("move-geometry")
            .withModel(models);
          firstOperation.set("text", "Add to Layer");
        }

        mobileContext = [
          firstOperation,
          self._createCloseMenuOperation(),
          true,
        ];
      }

      var contextMenu = mobileContext.concat([
        self._context.getOperation("copy").withModel(models),
        self._context.getOperation("paste").withModel(models),
        self._context.getOperation("paste-data").withModel(models),
        self._context.getOperation("remove-geometry").withModel(models),
      ]);

      if (!isMobile) {
        contextMenu = contextMenu.concat([
          true,
          self._context
            .getOperation("create-extent-geometry")
            .withModel(models),
          self._context
            .getOperation("create-buffer-geometry")
            .withModel(models),
          self._context.getOperation("join-geometry").withModel(models),
          self._context.getOperation("split-geometry").withModel(models),
          self._createVertexRemoveOperation(graphic, point, models[0]),
        ]);

        cms.attachContextMenu(
          clickContainerWithinMap,
          contextMenu,
          self.mapView.map.root
        );
      } else {
        var targetLocation = GeoUtils.getScreenPoint(self.mapView.map, graphic);

        cms.displayContextMenu(
          targetLocation.x,
          targetLocation.y + self.mapView.$el.offset().top,
          contextMenu
        );
      }
    }
  },

  /**
   * Attaches event handlers to the given layer configuration.
   * @param  {Layout} config The layer configuration.
   */
  _attachLayout: function (config) {
    this._attachLayer(null, null, config.workingLayer);

    _.each(
      config.serviceLayers,
      function (serviceLayer) {
        this._attachServiceLayer(serviceLayer);
      },
      this
    );

    this.listenTo(config, "service-added", this._attachServiceLayer);
    this.listenTo(config, "service-removed", this._detachServiceLayer);
    this.listenTo(config, "layer-added", this._attachLayer);
    this.listenTo(config, "layer-removed", this._detachLayer);
    this.listenTo(config, "service-loaded", this._zoomIfOnlyService);
  },

  /**
   * Detaches event handlers from the given layer configuration.
   * @param  {Layout} config The layer configuration.
   */
  _detachLayout: function (config) {
    this._detachLayer(null, null, config.workingLayer);

    _.each(
      config.serviceLayers,
      function (serviceLayer) {
        this._detachServiceLayer(serviceLayer);
      },
      this
    );

    this.stopListening(config);
  },

  _addServiceLayer: function (serviceLayer, index) {
    var promise = this.getConfiguration().addService(serviceLayer, index);

    promise = $.when(promise, session.user.fetch());

    return promise;
  },

  _zoomIfOnlyService: function (serviceLayer) {
    var activeServiceLayers = this.getConfiguration().serviceLayers;

    if (activeServiceLayers && activeServiceLayers.length == 1) {
      this._context.getOperation("zoom-to-item").action(serviceLayer);
    }
  },

  //remove a service layer FROM THE GUI ONLY
  _removeServiceLayer: function (serviceLayer) {
    this.getConfiguration().removeService(serviceLayer);
  },

  /**
   * Detach event handlers from the given service.
   * @private
   */
  _detachServiceLayer: function () {
    this.propertiesWindow.hide();
  },

  /**
   * Attach event handlers to the given service.
   * @private
   * @param  {MapServiceBase} serviceLayer The service.
   */
  _attachServiceLayer: function (serviceLayer) {},

  //remove a layer FROM THE GUI ONLY
  _removeLayer: function (layer) {
    this.getConfiguration().removeLayer(layer.serviceLayer, layer);
  },

  /**
   * Attaches a `layer` to this view. When a `layer` is attached, it is added to all views which list layers, when appropriate, event handlers are attached for various events on the layer, and child layers of the given `layer` are added.
   * @param {MapServiceBase} serviceLayer The service containing the layer to attach.
   * @param {MapLayerBase} parentLayer The parent layer of the layer to attach.
   * @param  {MapLayerBase} layer        The layer to attach.
   */
  _attachLayer: function (serviceLayer, parentLayer, layer) {
    this.listenTo(layer, "model-removed", function () {
      this._context.display(false);
    });
    if (serviceLayer && !serviceLayer.service.get("external")) {
      this.listenTo(layer, "model-added", function (geometry) {
        var feature = geometry.getFeature();

        geometry = feature.getGeometry();

        if (feature.isNew()) {
          geometry.originalLayer = layer;
          this._context
            .getOperation("commit-geometry")
            .action([feature.getGeometry()]);
        }
      });
    }
  },

  _detachLayer: function (serviceLayer, parentLayer, layer) {
    this.stopListening(layer);
    this._context.display(false);
  },

  _attachLayerView: function (layerView) {
    var self = this;

    this.listenTo(layerView, "layer-check", function (layer, options) {
      if (layer) {
        if (options.inherited) {
          //if this is being echoed down the tree, we don't want to change the actual value in the layer model
          if (layer.layer) {
            layer.layer.setVisibility(true);
          }
        } else {
          layer.show();
        }
      }
    });

    this.listenTo(layerView, "service-check", function (service) {
      if (service) {
        service.show();
      }
    });

    this.listenTo(layerView, "layer-uncheck", function (layer, options) {
      if (layer) {
        self.mapView.deactivateEdit(true);
        if (options.inherited) {
          //if this is being echoed down the tree, we don't want to change the actual value in the layer model
          if (layer.layer) {
            layer.layer.setVisibility(false);
          }
        } else {
          layer.hide();
        }
      }
    });

    this.listenTo(layerView, "service-uncheck", function (service) {
      if (service) {
        self.mapView.deactivateEdit(true);
        service.hide();
      }
    });

    this.listenTo(layerView, "layer-select", function (layer) {
      if (layer) {
        self._context.setSelectedLayer(layer);
        self._context
          .getOperation("view-item")
          .action(self._context.getSelectedLayer());
      }
    });

    this.listenTo(layerView, "service-select", function (service) {
      if (service) {
        self._context.setSelectedService(service);
        self._context
          .getOperation("view-item")
          .action(self._context.getSelectedService());
      }
    });

    this.listenTo(layerView, "model-select", function (mapObject, workspace) {
      self._showModel(mapObject, workspace);
    });

    this.listenTo(layerView, "model-removed", function () {
      self.propertiesWindow.hide();
    });

    this.listenTo(layerView, "service-doubleclick", function (service) {
      if (service) {
        self._context.getOperation("zoom-to-item").action(service);
      }
    });

    this.listenTo(layerView, "layer-doubleclick", function (layer) {
      if (layer) {
        self._context.getOperation("zoom-to-item").action(layer);
      }
    });

    this.listenTo(layerView, "model-doubleclick", function (mapObject) {
      if (mapObject) {
        self._context.getOperation("zoom-to-geometry").action([mapObject]);
      }
    });
  },

  _clearSelection: function () {
    return this._context.operations.get("clear-selection").action();
  },

  /**
   * Gets a value indicating whether or not the drawing mode is active on any layer for which it is applicable.
   * @return {Boolean} True if drawing mode is active, otherwise false.
   */
  isDrawingActive: function () {
    return this.mapView.isDrawingActive();
  },

  /**
   * Deactivates the draw mode for all layers to which it is applicable.
   */
  deactivateDraw: function () {
    this.mapView.deactivateDraw();
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/MapIndex.js
// module id = 942
// module chunks = 1